/**
 * DOM指标空状态监控系统 - 简化数据上报器
 */

import type { 
  IElementConfig, 
  IEnhancedElementConfig,
  ISimpleMetricsReport, 
  TEmptyStateType
} from '../types/simple';

/**
 * 简化的数据上报器
 * 负责防重复上报和数据上报
 */
export class SimpleMetricsReporter {
  private reportHistory = new Map<string, number>();
  private reportInterval: number;

  constructor(reportInterval: number = 10 * 60 * 1000) {
    this.reportInterval = reportInterval;
  }

  /**
   * 上报空指标数据（包含统计信息）
   */
  async reportEmptyMetrics(
    elements: IEnhancedElementConfig[], 
    statistics: {
      totalElements: number;
      emptyElements: number;
      emptyRatio: number;
    },
    context: {
      pageUrl?: string;
      pagePath?: string;
      pageTitle?: string;
    } = {}
  ): Promise<boolean> {
    
    if (elements.length === 0) {
      return false;
    }

    try {
      // 1. 过滤掉重复上报的元素
      // const filteredElements = this.filterDuplicateElements(elements);
      
      // if (filteredElements.length === 0) {
      //   console.log('[SimpleMetricsReporter] 所有元素都已在近期上报过，跳过');
      //   return false;
      // }

      // 2. 构建上报数据
      const reportData = this.buildReportData(elements, statistics, context);
      
      // 3. 执行上报
      await this.sendToAegis(reportData);
      
      // 4. 记录上报历史，暂时屏蔽上报历史
      // this.recordReportHistory(filteredElements);
      
      // console.log(`[SimpleMetricsReporter] 成功上报 ${filteredElements.length} 个空指标`);
      return true;
      
    } catch (error) {
      console.error('[SimpleMetricsReporter] 上报失败:', error);
      return false;
    }
  }

  /**
   * 过滤重复元素
   */
  private filterDuplicateElements(elements: IEnhancedElementConfig[]): IEnhancedElementConfig[] {
    const now = Date.now();
    
    return elements.filter(element => {
      const reportKey = this.generateReportKey(element);
      const lastReportTime = this.reportHistory.get(reportKey);
      
      // 如果从未上报过，或者距离上次上报已超过间隔时间
      return !lastReportTime || (now - lastReportTime) > this.reportInterval;
    });
  }

  /**
   * 生成上报唯一键
   */
  private generateReportKey(element: IEnhancedElementConfig): string {
    // 使用元素名称和选择器生成唯一键
    const selector = element.selector.replace(/\s+/g, '');
    return `${element.name}_${selector}`;
  }

  /**
   * 构建上报数据
   */
  private buildReportData(
    elements: IEnhancedElementConfig[], 
    statistics: {
      totalElements: number;
      emptyElements: number;
      emptyRatio: number;
    },
    context: {
      pageUrl?: string;
      pagePath?: string; 
      pageTitle?: string;
    }
  ): ISimpleMetricsReport {
    
    return {
      reportId: this.generateReportId(),
      reportType: 'metrics_empty',
      timestamp: Date.now(),
      
      page: {
        url: context.pageUrl || window.location.href,
        path: context.pagePath || window.location.pathname,
        title: context.pageTitle || document.title
      },
      
      statistics: {
        totalElements: statistics.totalElements,
        emptyElements: statistics.emptyElements,
        emptyRatio: statistics.emptyRatio
      },
      
      emptyMetrics: elements.map(element => ({
        elementId: element.elementId,
        elementName: element.name,
        selector: element.selector,
        // emptyType: this.detectEmptyType(element.element),
        emptyReason: element.emptyReason,
        detectedAt: element.detectedAt,
        tagName: element.tagName,
        className: element.className,
        textContent: element.textContent,
        childrenCount: element.childrenCount,
        businessData: element.businessData
      })),
      
      environment: {
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        }
      }
    };
  }

  /**
   * 检测空状态类型
   */
  private detectEmptyType(element: Element): TEmptyStateType {
    const text = element.textContent?.trim() || '';
    
    // 检查空状态类名
    const emptyClasses = ['no-data', 'empty-state', 'dbo-no-data', 'empty-content'];
    if (emptyClasses.some(cls => element.classList.contains(cls))) {
      return 'empty_class';
    }
    
    // 检查文本内容
    if (!text || text === '-' || text === '--' || text === 'N/A') {
      return 'no_content';
    }
    
    // 检查子元素
    const validChildren = Array.from(element.children).filter(child => {
      const style = window.getComputedStyle(child);
      return style.display !== 'none' && 
             style.visibility !== 'hidden' &&
             !child.classList.contains('loading');
    });
    
    if (validChildren.length === 0) {
      return 'no_children';
    }
    
    return 'has_content';
  }

  /**
   * 发送数据到Aegis
   */
  private async sendToAegis(reportData: ISimpleMetricsReport): Promise<void> {
    try {
      const aegis = (window as any)?.aegis;
      
      if (!aegis?.reportEvent) {
        console.warn('[SimpleMetricsReporter] Aegis未初始化，使用console输出');
        console.log('[SimpleMetricsReporter] 上报数据:', reportData);
        return;
      }

      // console.log('🚀 ~ SimpleMetricsReporter ~ sendToAegis ~ reportData:', reportData);

      // 发送到Aegis监控平台
      // const emptyPercentage = `${(reportData.statistics.emptyRatio * 100).toFixed(1)}%`;
      aegis.reportEvent({
        name: 'metrics_empty_detected',
        ext1: JSON.stringify({path: reportData.page.path, ...reportData.statistics}),
        ext2: JSON.stringify(reportData.emptyMetrics.map(metric => ({
          name: metric.elementName,
          // type: metric.emptyType,
          selector: metric.selector,
          reason: metric.emptyReason,
          // tagName: metric.tagName,
          // className: metric.className,
          // childrenCount: metric.childrenCount,
          detectedAt: metric.detectedAt
        }))),
        // ext3: `empty:${reportData.statistics.emptyElements}/total:${reportData.statistics.totalElements}/ratio:${emptyPercentage}`,
        // ext3: JSON.stringify(reportData.statistics)
      });
      // console.log('[SimpleMetricsReporter] 已发送到Aegis监控平台');
      
    } catch (error) {
      console.error('[SimpleMetricsReporter] Aegis上报失败:', error);
      throw error;
    }
  }

  /**
   * 记录上报历史
   */
  private recordReportHistory(elements: IEnhancedElementConfig[]): void {
    const now = Date.now();
    
    elements.forEach(element => {
      const reportKey = this.generateReportKey(element);
      this.reportHistory.set(reportKey, now);
    });
  }

  /**
   * 生成上报ID
   */
  private generateReportId(): string {
    return `metrics_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * 清理历史记录
   */
  cleanupHistory(): void {
    const cutoffTime = Date.now() - (24 * 60 * 60 * 1000); // 24小时
    let cleanedCount = 0;
    
    // 使用Array.from来避免IterableIterator的兼容性问题
    const entries = Array.from(this.reportHistory.entries());
    for (const [key, time] of entries) {
      if (time < cutoffTime) {
        this.reportHistory.delete(key);
        cleanedCount++;
      }
    }
    
    console.log(`[SimpleMetricsReporter] 清理了 ${cleanedCount} 个历史记录`);
  }

  /**
   * 获取统计信息
   */
  getStats(): {
    historyCount: number;
    reportInterval: number;
  } {
    return {
      historyCount: this.reportHistory.size,
      reportInterval: this.reportInterval
    };
  }

  /**
   * 更新上报间隔
   */
  updateReportInterval(interval: number): void {
    this.reportInterval = interval;
    // console.log(`[SimpleMetricsReporter] 上报间隔已更新为 ${interval}ms`);
  }

  /**
   * 销毁上报器
   */
  destroy(): void {
    this.reportHistory.clear();
    console.log('[SimpleMetricsReporter] 上报器已销毁');
  }
} 